import { homeStats } from '@/api/home'
import {
  projectList,
  buildingList,
  floorList,
  deviceList,
  baseStationList
} from '@/api'
import resource from '@/mock/store'
import {
  assembleProjectTree,
  apiProjectToUi,
  apiAdminToUi,
  apiDeviceToBeacon,
  apiBaseStationToUi,
  buildingsToZones
} from '@/api/adapters'
import { loadAdminList } from './adminService'
import { loadJobTypes, resetJobTypesCache } from './jobTypeService'

const PAGE = { page: 1, page_size: 100 }
const BASE_STATION_PAGE = { page: 1, page_size: 999 }

export async function fetchBaseStationList(params = {}) {
  const res = await baseStationList({ ...BASE_STATION_PAGE, ...params })
  return (res.data && res.data.list) || []
}

export async function fetchAllPages(fetcher, params = {}) {
  const first = await fetcher({ ...PAGE, ...params })
  const data = first.data || {}
  let list = data.list || []
  const total = data.total || list.length
  if (total > list.length) {
    const pages = Math.ceil(total / PAGE.page_size)
    const tasks = []
    for (let page = 2; page <= pages; page++) {
      tasks.push(fetcher({ ...params, page, page_size: PAGE.page_size }))
    }
    const rest = await Promise.all(tasks)
    rest.forEach(r => {
      list = list.concat((r.data && r.data.list) || [])
    })
  }
  return list
}

let projectListParams = {}

export function setProjectListParams(params = {}) {
  projectListParams = { ...params }
}

export function getProjectListParams() {
  return { ...projectListParams }
}

function buildProjectListQuery(params = {}) {
  const query = {}
  const keyword = params.keyword != null ? String(params.keyword).trim() : ''
  if (keyword) query.keyword = keyword
  if (params.type) query.type = params.type
  return query
}

function deviceContext() {
  return {
    projects: resource.projects,
    buildings: (resource.apiBuildings || []).filter(Boolean),
    floors: (resource.apiFloors || []).filter(Boolean).map(f => ({
      id: f.id,
      name: f.name || ''
    })),
    jobTypes: resource.jobTypes || []
  }
}

function mapDevicesToBeacons(devices, ctx = deviceContext()) {
  return (devices || [])
    .filter(Boolean)
    .map(d => apiDeviceToBeacon(d, ctx))
    .filter(Boolean)
}

function applyProjectTree(projects, buildings, floors) {
  const uiProjects = assembleProjectTree(projects, buildings, floors)
  resource.projects.splice(0, resource.projects.length, ...uiProjects)
  resource.apiProjects = projects
  resource.apiBuildings = buildings
  resource.apiFloors = floors
  resource.zones = buildingsToZones(buildings, projects)
  return uiProjects
}

function applyFlatProjects(projects) {
  const uiProjects = projects.map(p => apiProjectToUi(p))
  resource.projects.splice(0, resource.projects.length, ...uiProjects)
  resource.apiProjects = projects
  return uiProjects
}

/** 刷新项目列表时保留已加载的工区树（区域/楼栋/楼层），避免看板详情被 flat 覆盖 */
function mergeFlatProjects(projects) {
  const assembledById = new Map()
  resource.projects.forEach(p => {
    const pid = Number(p.id)
    if ((p.regions && p.regions.length) || (p.floors && p.floors.length)) {
      assembledById.set(pid, p)
    }
  })
  const uiProjects = projects.map(p => assembledById.get(Number(p.id)) || apiProjectToUi(p))
  resource.projects.splice(0, resource.projects.length, ...uiProjects)
  resource.apiProjects = projects
  return uiProjects
}

let homeStatsInflight = null
let homeStatsInflightKey = ''

/** 首页 KPI；支持 project_id / building_id / floor_id 分层过滤 */
export async function loadHomeStats(params = {}) {
  const pid = Number(params.project_id)
  const hasBuilding = params.building_id != null && params.building_id !== ''
  const hasFloor = params.floor_id != null && params.floor_id !== ''
  const bid = hasBuilding ? Number(params.building_id) : null
  const fid = hasFloor ? Number(params.floor_id) : null
  const key = pid > 0
    ? `p${pid}_b${hasBuilding ? bid : 'x'}_f${hasFloor ? fid : 'x'}`
    : 'global'
  if (homeStatsInflight && homeStatsInflightKey === key) {
    return homeStatsInflight
  }
  homeStatsInflightKey = key
  const payload = {}
  if (pid > 0) payload.project_id = pid
  if (hasBuilding) payload.building_id = bid
  if (hasFloor) payload.floor_id = fid
  homeStatsInflight = homeStats(payload)
    .catch(() => ({ data: {} }))
    .then(statsRes => {
      const stats = statsRes.data || {}
      Object.assign(resource.homeStats, {
        project_total: stats.project_total || 0,
        device_total: stats.device_total || 0,
        device_online_count: stats.device_online_count || 0,
        base_station_total: stats.base_station_total || 0,
        base_station_online_count: stats.base_station_online_count || 0
      })
      return resource.homeStats
    })
    .finally(() => {
      if (homeStatsInflightKey === key) {
        homeStatsInflight = null
      }
    })
  return homeStatsInflight
}

/** 看板首页：项目列表（KPI 由 Dashboard 按当前层级自行拉取） */
async function loadDashboard() {
  const projects = await fetchAllPages(projectList)
  await loadJobTypes().catch(() => {})
  mergeFlatProjects(projects)
}

/** 项目列表分页 */
export async function fetchProjectListPage(params = {}) {
  const payload = {
    page: Number(params.page) || 1,
    page_size: Number(params.page_size) || 20
  }
  const listQuery = buildProjectListQuery(params)
  Object.assign(payload, listQuery)
  const res = await projectList(payload)
  const data = res.data || {}
  const rawList = data.list || []
  return {
    list: rawList.map(p => apiProjectToUi(p)),
    apiList: rawList,
    total: Number(data.total) || rawList.length,
    page: Number(data.page) || payload.page,
    page_size: Number(data.page_size) || payload.page_size
  }
}

/** 工区页进入：项目列表第一页 */
async function loadZone() {
  const result = await fetchProjectListPage({ ...projectListParams, page: 1, page_size: 20 })
  applyFlatProjects(result.apiList || [])
  resource.apiBuildings = []
  resource.apiFloors = []
  resource.stations.splice(0, resource.stations.length)
  return result
}

/** 工区：选中项目后加载 Building + Floor（不拉基站列表） */
export async function loadZoneProjectDetail(projectId) {
  if (!projectId) return null
  const pid = Number(projectId)
  const apiProject = resource.apiProjects.find(p => Number(p.id) === pid)
  if (!apiProject) return null

  const [buildings, floors] = await Promise.all([
    fetchAllPages(buildingList, { project_id: pid }),
    fetchAllPages(floorList, { project_id: pid })
  ])

  const uiProject = assembleProjectTree([apiProject], buildings, floors)[0]
  const idx = resource.projects.findIndex(p => Number(p.id) === pid)
  if (idx >= 0) resource.projects.splice(idx, 1, uiProject)

  resource.apiBuildings = [
    ...resource.apiBuildings.filter(b => Number(b.project_id) !== pid),
    ...buildings
  ]
  resource.apiFloors = [
    ...resource.apiFloors.filter(f => Number(f.project_id) !== pid),
    ...floors
  ]

  return uiProject
}

/** 看板：按项目/楼层拉在线设备 */
export async function loadDashboardBeacons(params = {}) {
  const query = { online_status: 1 }
  if (params.floorId) {
    query.floor_id = Number(params.floorId)
  } else if (params.projectId) {
    query.project_id = Number(params.projectId)
  } else {
    resource.beacons.splice(0, resource.beacons.length)
    return []
  }
  const devices = await fetchAllPages(deviceList, query)
  const list = mapDevicesToBeacons(devices)
  resource.beacons.splice(0, resource.beacons.length, ...list)
  return list
}

/** 看板/工区：按项目加载安全帽（设备，含离线） */
export async function loadProjectBeacons(projectId) {
  const pid = Number(projectId)
  if (!pid) {
    resource.beacons.splice(0, resource.beacons.length)
    return []
  }
  const devices = await fetchAllPages(deviceList, { project_id: pid })
  const list = mapDevicesToBeacons(devices)
  resource.beacons.splice(0, resource.beacons.length, ...list)
  return list
}

/** 工区图纸工作台：按项目 + 层级加载基站 */
export async function loadProjectStations(projectId, scope = {}) {
  const pid = Number(projectId)
  if (!pid) {
    resource.stations.splice(0, resource.stations.length)
    return []
  }
  const params = {
    project_id: pid,
    building_id: Number(scope.buildingId) || 0,
    floor_id: Number(scope.floorId) || 0
  }
  const stations = await fetchBaseStationList(params)
  const list = stations.map(s => apiBaseStationToUi(s, { projects: resource.projects }))
  resource.stations.splice(0, resource.stations.length, ...list)
  return list
}

/** 工区：拉取未绑定基站（BaseStation/List：page + page_size + only_unbound） */
export async function loadUnboundStations(params = {}) {
  const payload = {
    page: Number(params.page) || 1,
    page_size: Number(params.page_size) || 10,
    only_unbound: 1
  }
  const keyword = (params.keyword || '').trim()
  if (keyword) payload.keyword = keyword
  const res = await baseStationList(payload)
  const data = res.data || {}
  const list = (data.list || []).map(s => apiBaseStationToUi(s, { projects: resource.projects }))
  return {
    list,
    total: Number(data.total) || 0,
    page: Number(data.page) || payload.page,
    page_size: Number(data.page_size) || payload.page_size
  }
}

/** 工区项目搜索：Project/List（传 page/page_size 时分页，否则全量） */
export async function searchProjectList(params = {}) {
  const { page, page_size, ...rest } = params
  projectListParams = { ...rest }
  if (page != null || page_size != null) {
    const result = await fetchProjectListPage({
      ...projectListParams,
      page: page || 1,
      page_size: page_size || 20
    })
    applyFlatProjects(result.apiList || [])
    resource.apiBuildings = []
    resource.apiFloors = []
    resource.stations.splice(0, resource.stations.length)
    return result
  }
  const listQuery = buildProjectListQuery(projectListParams)
  const projects = await fetchAllPages(projectList, listQuery)
  applyFlatProjects(projects)
  resource.apiBuildings = []
  resource.apiFloors = []
  resource.stations.splice(0, resource.stations.length)
  return resource.projects
}

/** 工区 CRUD 后：刷新项目列表 */
export async function refreshProjectList() {
  return loadZone()
}

/** 工区 CRUD 后：刷新单个项目的工区树 */
export async function refreshZoneProjectDetail(projectId) {
  return loadZoneProjectDetail(projectId)
}

let devicePageParams = null
let stationPageParams = null

export function setDevicePageParams(params) {
  devicePageParams = params ? { ...params } : null
}

export function setStationPageParams(params) {
  stationPageParams = params ? { ...params } : null
}

/** 安全帽列表分页 */
export async function fetchDeviceListPage(params = {}) {
  const payload = {
    page: Number(params.page) || 1,
    page_size: Number(params.page_size) || 10
  }
  const keyword = (params.keyword || '').trim()
  if (keyword) payload.keyword = keyword
  if (params.online_status != null && params.online_status !== '') {
    payload.online_status = Number(params.online_status)
  }
  const projectId = Number(params.project_id)
  if (projectId > 0) payload.project_id = projectId
  if (params.building_id != null && params.building_id !== '') {
    payload.building_id = Number(params.building_id)
  }
  if (params.floor_id != null && params.floor_id !== '') {
    payload.floor_id = Number(params.floor_id)
  }
  const res = await deviceList(payload)
  const data = res.data || {}
  const list = mapDevicesToBeacons(data.list || [])
  return {
    list,
    total: Number(data.total) || 0,
    page: Number(data.page) || payload.page,
    page_size: Number(data.page_size) || payload.page_size
  }
}

/** 基站列表分页 */
export async function fetchStationListPage(params = {}) {
  const payload = {
    page: Number(params.page) || 1,
    page_size: Number(params.page_size) || 10
  }
  const keyword = (params.keyword || '').trim()
  if (keyword) payload.keyword = keyword
  if (params.only_unbound === 1) payload.only_unbound = 1
  else if (params.only_unbound === 0) payload.only_unbound = 0
  if (params.online_status != null && params.online_status !== '') {
    payload.online_status = Number(params.online_status)
  }
  const projectId = Number(params.project_id)
  if (projectId > 0) payload.project_id = projectId
  if (params.building_id != null && params.building_id !== '') {
    payload.building_id = Number(params.building_id)
  }
  if (params.floor_id != null && params.floor_id !== '') {
    payload.floor_id = Number(params.floor_id)
  }
  const res = await baseStationList(payload)
  const data = res.data || {}
  const list = (data.list || []).map(s => apiBaseStationToUi(s, { projects: resource.projects }))
  return {
    list,
    total: Number(data.total) || 0,
    page: Number(data.page) || payload.page,
    page_size: Number(data.page_size) || payload.page_size
  }
}

/** 设备页：安全帽列表（分页上下文） */
export async function loadDeviceBeaconsPage(params = {}) {
  const result = await fetchDeviceListPage(params)
  resource.beacons.splice(0, resource.beacons.length, ...result.list)
  return result
}

/** 设备页：基站列表（分页上下文） */
export async function loadDeviceStationsPage(params = {}) {
  const result = await fetchStationListPage(params)
  resource.stations.splice(0, resource.stations.length, ...result.list)
  return result
}

/** @deprecated 全量拉取，供旧逻辑兼容 */
export async function loadDeviceBeacons() {
  await loadJobTypes().catch(() => {})
  const devices = await fetchAllPages(deviceList)
  resource.beacons.splice(0, resource.beacons.length, ...mapDevicesToBeacons(devices))
  return resource.beacons
}

/** @deprecated 全量拉取，供旧逻辑兼容 */
export async function loadDeviceStations() {
  const stations = await fetchBaseStationList()
  resource.stations.splice(
    0,
    resource.stations.length,
    ...stations.map(s => apiBaseStationToUi(s, { projects: resource.projects }))
  )
  return resource.stations
}

let workStatsZoneTreeLoaded = false

async function loadWorkStats() {
  const listQuery = buildProjectListQuery(projectListParams)
  const projects = await fetchAllPages(projectList, listQuery)
  applyFlatProjects(projects)
}

/** 作业统计：工区树（按需加载） */
export async function ensureWorkStatsZoneTree() {
  if (workStatsZoneTreeLoaded && resource.apiBuildings.length) return resource.projects
  const [projects, buildings, floors] = await Promise.all([
    fetchAllPages(projectList),
    fetchAllPages(buildingList),
    fetchAllPages(floorList)
  ])
  applyProjectTree(projects, buildings, floors)
  workStatsZoneTreeLoaded = true
  return resource.projects
}

/** 作业统计：安全帽列表（按需加载） */
export async function ensureWorkStatsBeacons() {
  if (resource.beacons.length) return resource.beacons
  await loadJobTypes().catch(() => {})
  const devices = await fetchAllPages(deviceList)
  resource.beacons.splice(0, resource.beacons.length, ...mapDevicesToBeacons(devices))
  return resource.beacons
}

let deviceFormOptionsLoaded = false

/** 设备页：表单/绑定弹窗所需 Project + Building + Floor */
export async function loadDeviceFormOptions() {
  if (deviceFormOptionsLoaded) return resource.projects
  const [projects, buildings, floors] = await Promise.all([
    fetchAllPages(projectList),
    fetchAllPages(buildingList),
    fetchAllPages(floorList)
  ])
  applyProjectTree(projects, buildings, floors)
  deviceFormOptionsLoaded = true
  return resource.projects
}

export function resetDevicePageCache() {
  deviceFormOptionsLoaded = false
}

/** @deprecated 使用 loadDeviceBeacons / loadDeviceStations */
async function loadDevice() {
  return loadDeviceBeacons()
}

/** 轨迹：项目树 + 设备列表 */
async function loadTrace() {
  const [projects, buildings, floors, devices] = await Promise.all([
    fetchAllPages(projectList),
    fetchAllPages(buildingList),
    fetchAllPages(floorList),
    fetchAllPages(deviceList)
  ])
  await loadJobTypes().catch(() => {})
  applyProjectTree(projects, buildings, floors)
  resource.beacons.splice(0, resource.beacons.length, ...mapDevicesToBeacons(devices))
}

/** CRUD 后：刷新项目树（Project + Building + Floor） */
export async function refreshProjectTree(params, options = {}) {
  const persist = options.persistParams !== false
  const q = params !== undefined ? params : projectListParams
  const listQuery = buildProjectListQuery(q)
  const [projects, buildings, floors] = await Promise.all([
    fetchAllPages(projectList, listQuery),
    fetchAllPages(buildingList),
    fetchAllPages(floorList)
  ])
  if (params !== undefined && persist) projectListParams = { ...q }
  return applyProjectTree(projects, buildings, floors)
}

/** CRUD 后：仅刷新信标（设备页走分页参数） */
export async function refreshDevices() {
  if (devicePageParams) {
    return loadDeviceBeaconsPage(devicePageParams)
  }
  await loadJobTypes().catch(() => {})
  const devices = await fetchAllPages(deviceList)
  resource.beacons.splice(0, resource.beacons.length, ...mapDevicesToBeacons(devices))
  return resource.beacons
}

/** CRUD 后：仅刷新基站（设备页走分页参数） */
export async function refreshStations() {
  if (stationPageParams) {
    return loadDeviceStationsPage(stationPageParams)
  }
  const stations = await fetchBaseStationList()
  resource.stations.splice(
    0,
    resource.stations.length,
    ...stations.map(s => apiBaseStationToUi(s, { projects: resource.projects }))
  )
  return resource.stations
}

/** @deprecated 仅兼容旧调用，内部不再使用 */
export function syncProjects(params, options) {
  return refreshProjectTree(params, options)
}

export function syncDevices() {
  return refreshDevices()
}

export function syncStations() {
  return refreshStations()
}

export function syncResource() {
  return Promise.all([refreshProjectTree(), refreshDevices(), refreshStations()])
}

/** 账号切换/登出时清空共享缓存，避免下一账号读到上一账号数据 */
export function resetSharedResource() {
  resource.projects.splice(0, resource.projects.length)
  resource.beacons.splice(0, resource.beacons.length)
  resource.stations.splice(0, resource.stations.length)
  resource.accounts.splice(0, resource.accounts.length)
  resource.jobTypes.splice(0, resource.jobTypes.length)
  resource.alarms.splice(0, resource.alarms.length)
  resource.enterLeaveRecords.splice(0, resource.enterLeaveRecords.length)
  resource.zones.splice(0, resource.zones.length)
  resource.apiProjects.splice(0, resource.apiProjects.length)
  resource.apiBuildings.splice(0, resource.apiBuildings.length)
  resource.apiFloors.splice(0, resource.apiFloors.length)
  Object.assign(resource.homeStats, {
    project_total: 0,
    device_total: 0,
    device_online_count: 0,
    base_station_total: 0,
    base_station_online_count: 0
  })

  projectListParams = {}
  homeStatsInflight = null
  homeStatsInflightKey = ''
  workStatsZoneTreeLoaded = false
  deviceFormOptionsLoaded = false
  devicePageParams = null
  stationPageParams = null
  resetJobTypesCache()

  try {
    localStorage.removeItem('workstats_location_project_id')
  } catch (e) {
    /* ignore */
  }
}

/** 路由进入时：各页只拉自己用到的接口 */
export function loadPageData(routeName, user) {
  switch (routeName) {
    case 'Dashboard':
      return loadDashboard()
    case 'ZoneManage':
      return loadZone()
    case 'DeviceManage':
      resetDevicePageCache()
      setDevicePageParams(null)
      setStationPageParams(null)
      resource.stations.splice(0, resource.stations.length)
      resource.beacons.splice(0, resource.beacons.length)
      return loadJobTypes().catch(() => {})
    case 'AlarmManage':
      return Promise.resolve()
    case 'EnterLeaveManage':
      return loadJobTypes().catch(() => {})
    case 'JobTypeManage':
      return loadJobTypes()
    case 'TracePlayback':
      return loadTrace()
    case 'WorkStats':
      return loadWorkStats()
    case 'SystemSettings':
      return loadAdminList(user)
    default:
      return Promise.resolve()
  }
}

export function setAccounts(list) {
  resource.accounts.splice(0, resource.accounts.length, ...list.map(apiAdminToUi))
}

export function upsertAccount(admin) {
  const ui = apiAdminToUi(admin)
  const idx = resource.accounts.findIndex(a => a.id === ui.id)
  if (idx > -1) resource.accounts.splice(idx, 1, ui)
  else resource.accounts.push(ui)
}

export function removeAccounts(ids) {
  const idSet = new Set(ids)
  for (let i = resource.accounts.length - 1; i >= 0; i--) {
    if (idSet.has(resource.accounts[i].id)) resource.accounts.splice(i, 1)
  }
}

export default {
  resetSharedResource,
  loadPageData,
  loadHomeStats,
  loadDeviceBeaconsPage,
  loadDeviceStationsPage,
  fetchDeviceListPage,
  fetchStationListPage,
  fetchProjectListPage,
  setDevicePageParams,
  setStationPageParams,
  loadDeviceFormOptions,
  resetDevicePageCache,
  searchProjectList,
  loadZoneProjectDetail,
  refreshProjectList,
  refreshZoneProjectDetail,
  refreshProjectTree,
  refreshDevices,
  refreshStations,
  syncProjects,
  syncDevices,
  syncStations,
  syncResource,
  setProjectListParams,
  getProjectListParams,
  fetchAllPages,
  fetchBaseStationList,
  ensureWorkStatsZoneTree,
  ensureWorkStatsBeacons,
  setAccounts,
  upsertAccount,
  removeAccounts
}
